import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
    Building2, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle,
    Landmark, Users, UserSquare2, Briefcase,
} from 'lucide-react';

import {
    fetchBasicBusinessInfo,
    selectBasicBusinessInfoData,
    selectBasicBusinessInfoLoading,
    selectBasicBusinessInfoError,
} from '../../slices/ReportsSlice/basicBusinessInfoSlice';

const ReportTable = ({ columns, rows, renderRow, emptyMessage }) => (
    <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600">
                <tr>
                    {columns.map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.length === 0 ? (
                    <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">{emptyMessage}</td></tr>
                ) : (
                    rows.map((row, i) => (
                        <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                            {renderRow(row, i)}
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    </div>
);

const td = (val, extra = '') => <td className={`px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line ${extra}`}>{val || '—'}</td>;

const SummaryCard = ({ icon: Icon, label, value, color, bg }) => (
    <div className={`bg-gradient-to-r ${bg} rounded-xl p-5 border border-gray-200 dark:border-gray-700`}>
        <div className="flex items-center justify-between">
            <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            </div>
            <div className={`p-3 bg-gradient-to-br ${color} rounded-xl`}>
                <Icon className="h-5 w-5 text-white" />
            </div>
        </div>
    </div>
);

const BasicBusinessInfoView = () => {
    const dispatch = useDispatch();
    const data = useSelector(selectBasicBusinessInfoData);
    const loading = useSelector(selectBasicBusinessInfoLoading);
    const error = useSelector(selectBasicBusinessInfoError);

    useEffect(() => { dispatch(fetchBasicBusinessInfo()); }, [dispatch]);
    useEffect(() => { if (error) toast.error(error); }, [error]);

    const firms        = data?.firminfoList || [];
    const capital       = data?.CapitalinfoList || [];
    const promoters      = data?.PromoterinfoList || [];
    const shareHolders  = data?.ShareHolderinfoList || [];
    const directors     = data?.DirectorsinfoList || [];

    const totalShareValue = useMemo(
        () => shareHolders.reduce((s, r) => s + (parseFloat(r.ShareHolderTotal) || 0), 0),
        [shareHolders]
    );

    const handleExport = () => {
        if (!firms.length && !capital.length && !promoters.length && !shareHolders.length && !directors.length) {
            toast.warning('No data to export'); return;
        }
        const wb = XLSX.utils.book_new();
        const addSheet = (name, rows) => {
            if (rows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name);
        };
        addSheet('Firm Details', firms.map((r) => ({ 'Firm Name': r.firmName, 'Firm Details': r.firmDescription, 'Firm Regions': r.Regionslist })));
        addSheet('Capital Details', capital.map((r) => ({ Type: r.CapitalType, Date: r.ShareCapitalDate, 'No of Shares': r.shares, 'Price Per Share': r.Eachshare, Amount: r.shareAmount })));
        addSheet('Promoter Details', promoters.map((r) => ({ Name: r.Promoternames, Address: r.PromoterAddress })));
        addSheet('Share Holder Details', shareHolders.map((r) => ({ Name: r.ShareHolderNames, '% Holding': r.ShareHolderPrecent, 'No of Shares': r.ShareHolderNoofShares, 'Share Price': r.ShareHolderSharePrice, 'Total Value': r.ShareHolderTotal })));
        addSheet('Directors Details', directors.map((r) => ({ Name: r.DirNames, Address: r.Diraddresses, Designation: r.DirDesignations })));
        XLSX.writeFile(wb, `Basic_Business_Info_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl">
                                <Building2 className="h-6 w-6 text-white" />
                            </div>
                            View Company Details
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Firm, capital, promoter, shareholder and director information
                        </p>
                        <nav className="flex items-center gap-1.5 text-xs text-gray-400 mt-2">
                            <span>Reports</span>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-gray-700 dark:text-gray-200">Basic Business Info</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => dispatch(fetchBasicBusinessInfo())}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                        <button
                            onClick={handleExport}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            <Download className="h-4 w-4" />
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-16">
                    <Loader2 className="h-7 w-7 animate-spin text-indigo-600" />
                    <span className="text-gray-500 dark:text-gray-400">Loading business info…</span>
                </div>
            ) : error ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-16 text-red-500">
                    <AlertTriangle className="h-6 w-6" />
                    <span>{error}</span>
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <SummaryCard icon={Landmark}     label="Capital Entries"  value={capital.length}      color="from-indigo-500 to-violet-600" bg="from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20" />
                        <SummaryCard icon={UserSquare2}  label="Promoters"        value={promoters.length}    color="from-blue-500 to-indigo-600"   bg="from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20" />
                        <SummaryCard icon={Users}        label="Total Shareholder Value" value={`₹${totalShareValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} color="from-emerald-500 to-teal-600" bg="from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20" />
                        <SummaryCard icon={Briefcase}    label="Directors"        value={directors.length}    color="from-amber-500 to-orange-600" bg="from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20" />
                    </div>

                    {/* Firm Details */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300">Firm Details</h2>
                        </div>
                        <ReportTable
                            columns={['Firm Name', 'Firm Details', 'Firm Regions']}
                            rows={firms}
                            emptyMessage="No firm details found."
                            renderRow={(item) => (<>{td(item.firmName, 'font-semibold text-gray-900 dark:text-white')}{td(item.firmDescription)}{td(item.Regionslist)}</>)}
                        />
                    </div>

                    {/* Capital Details */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300">Capital Details</h2>
                        </div>
                        <ReportTable
                            columns={['Type', 'Date', 'No of Shares', 'Price of Each Share', 'Amount']}
                            rows={capital}
                            emptyMessage="No capital details found."
                            renderRow={(item) => (<>{td(item.CapitalType, 'font-semibold text-gray-900 dark:text-white')}{td(item.ShareCapitalDate)}{td(item.shares)}{td(item.Eachshare)}{td(item.shareAmount, 'font-bold text-indigo-700 dark:text-indigo-400')}</>)}
                        />
                    </div>

                    {/* Promoter Details */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300">Promoter Details</h2>
                        </div>
                        <ReportTable
                            columns={['Name', 'Address']}
                            rows={promoters}
                            emptyMessage="No promoter details found."
                            renderRow={(item) => (<>{td(item.Promoternames, 'font-semibold text-gray-900 dark:text-white')}{td(item.PromoterAddress)}</>)}
                        />
                    </div>

                    {/* Share Holder Details */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300">Share Holder Details</h2>
                        </div>
                        <ReportTable
                            columns={['Name', '% Of Share Holding', 'No Of Shares', 'Each Share Price', 'Total Value']}
                            rows={shareHolders}
                            emptyMessage="No share holder details found."
                            renderRow={(item) => (<>{td(item.ShareHolderNames, 'font-semibold text-gray-900 dark:text-white')}{td(item.ShareHolderPrecent)}{td(item.ShareHolderNoofShares)}{td(item.ShareHolderSharePrice)}{td(item.ShareHolderTotal, 'font-bold text-indigo-700 dark:text-indigo-400')}</>)}
                        />
                    </div>

                    {/* Directors Details */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300">Directors Details</h2>
                        </div>
                        <ReportTable
                            columns={['Name', 'Address', 'Designation']}
                            rows={directors}
                            emptyMessage="No director details found."
                            renderRow={(item) => (<>{td(item.DirNames, 'font-semibold text-gray-900 dark:text-white')}{td(item.Diraddresses)}{td(item.DirDesignations)}</>)}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default BasicBusinessInfoView;
